/**
 * Main Layout with Sidebar Navigation
 */

import { useState, useEffect } from 'react';
import { Link, useLocation, Outlet, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  BarChart3,
  ClipboardList,
  Route,
  Map,
  Users,
  Calendar,
  Settings,
  ChevronLeft,
  ChevronRight,
  Menu,
  Code,
  Store,
  FlaskConical,
  LogOut,
  Navigation,
  MapPin,
  Truck,
  CalendarClock,
  CalendarCog,
  SearchCode,
  Timer,
  Sparkles,
} from 'lucide-react';
import { cn } from '../utils/cn';
import { useAuth } from '../contexts/useAuth';

interface NavItem {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  permission?: string;
}

const navigation: NavItem[] = [
  { name: 'Dashboard', href: '/', icon: LayoutDashboard, permission: 'dashboard.view' },
  { name: 'Analytics', href: '/analytics', icon: BarChart3, permission: 'analytics.view' },
  { name: 'Tasks', href: '/tasks', icon: ClipboardList, permission: 'tasks.view' },
  { name: 'Route Planning', href: '/routes', icon: Route, permission: 'routes.view' },
  { name: 'Shadow Plan', href: '/shadow-plan', icon: Sparkles, permission: 'shadow.view' },
  { name: 'Simulation', href: '/simulation', icon: FlaskConical, permission: 'simulation.view' },
  { name: 'Zones', href: '/zones', icon: Map, permission: 'zones.view' },
  { name: 'Drivers', href: '/drivers', icon: Users, permission: 'drivers.view' },
  { name: 'Merchant Time Slots', href: '/merchants', icon: Store, permission: 'merchants.view' },
  { name: 'Merchant Locations', href: '/merchant-locations', icon: MapPin, permission: 'merchants.view' },
  { name: 'Carrier Cutoffs', href: '/carrier-cutoffs', icon: Truck, permission: 'cutoffs.view' },
  { name: 'Pickup Slots', href: '/pickup-slots', icon: CalendarClock, permission: 'slots.view' },
  { name: 'Slot Settings', href: '/slot-settings', icon: CalendarCog, permission: 'slots.view' },
  { name: 'Driver Schedules', href: '/schedules', icon: Calendar, permission: 'schedules.view' },
  { name: 'Driver Monitor', href: '/driver-monitor', icon: Navigation, permission: 'drivers.view' },
  { name: 'Live Monitor', href: '/monitor', icon: MapPin, permission: 'routes.view' },
  { name: 'Working Hours', href: '/working-hours', icon: Timer, permission: 'working_hours.view' },
];

const bottomNavigation: NavItem[] = [
  { name: 'Task Lookup', href: '/task-lookup', icon: SearchCode, permission: 'tasks.view' },
  { name: 'API Logs', href: '/logs', icon: Code, permission: 'logs.view' },
  { name: 'Settings', href: '/settings', icon: Settings },
];

export default function MainLayout() {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, can } = useAuth();
  const [forbidden, setForbidden] = useState(false);
  useEffect(() => {
    let t: ReturnType<typeof setTimeout>;
    const on = () => {
      setForbidden(true);
      clearTimeout(t);
      t = setTimeout(() => setForbidden(false), 5000);
    };
    window.addEventListener('fleet:forbidden-action', on);
    return () => { window.removeEventListener('fleet:forbidden-action', on); clearTimeout(t); };
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (href: string) => {
    if (href === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(href);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {forbidden && (
        <div role="alert" className="fixed top-4 right-4 z-50 rounded-lg bg-red-600 px-4 py-3 text-sm text-white shadow-lg">
          You don't have permission to do this. Ask an admin for access.
        </div>
      )}
      {/* Mobile menu button */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-200 px-4 py-3">
        <div className="flex items-center justify-between">
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="p-2 rounded-md text-gray-500 hover:text-gray-900 hover:bg-gray-100"
          >
            <Menu className="h-6 w-6" />
          </button>
          <span className="text-lg font-semibold text-gray-900">Fleet Management</span>
          <div className="w-10" /> {/* Spacer */}
        </div>
      </div>

      {/* Mobile sidebar overlay */}
      {mobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 z-40 bg-black/50"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed top-0 left-0 z-40 h-screen bg-white border-r border-gray-200 transition-all duration-300',
          sidebarCollapsed ? 'w-16' : 'w-64',
          mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        )}
      >
        {/* Logo */}
        <div className="flex items-center justify-between h-16 px-4 border-b border-gray-200">
          {!sidebarCollapsed && (
            <span className="text-xl font-bold text-gray-900">Fleet</span>
          )}
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="hidden lg:flex p-1.5 rounded-md text-gray-500 hover:text-gray-900 hover:bg-gray-100"
          >
            {sidebarCollapsed ? (
              <ChevronRight className="h-5 w-5" />
            ) : (
              <ChevronLeft className="h-5 w-5" />
            )}
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex flex-col h-[calc(100vh-4rem)] px-2 py-4">
          <div className="flex-1 space-y-1">
            {navigation.filter((i) => !i.permission || can(i.permission)).map((item) => (
              <Link
                key={item.name}
                to={item.href}
                onClick={() => setMobileMenuOpen(false)}
                className={cn(
                  'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors',
                  isActive(item.href)
                    ? 'bg-blue-50 text-blue-700'
                    : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                )}
              >
                <item.icon
                  className={cn(
                    'h-5 w-5 flex-shrink-0',
                    isActive(item.href) ? 'text-blue-700' : 'text-gray-500'
                  )}
                />
                {!sidebarCollapsed && <span>{item.name}</span>}
              </Link>
            ))}
          </div>

          {/* Bottom navigation */}
          <div className="space-y-1 pt-4 border-t border-gray-200">
            {bottomNavigation.filter((i) => !i.permission || can(i.permission)).map((item) => (
              <Link
                key={item.name}
                to={item.href}
                onClick={() => setMobileMenuOpen(false)}
                className={cn(
                  'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors',
                  isActive(item.href)
                    ? 'bg-blue-50 text-blue-700'
                    : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                )}
              >
                <item.icon
                  className={cn(
                    'h-5 w-5 flex-shrink-0',
                    isActive(item.href) ? 'text-blue-700' : 'text-gray-500'
                  )}
                />
                {!sidebarCollapsed && <span>{item.name}</span>}
              </Link>
            ))}

            {/* User info and logout */}
            <div className="pt-3 mt-3 border-t border-gray-200">
              {!sidebarCollapsed && user && (
                <div className="px-3 py-2 text-xs text-gray-500">
                  <span className="font-medium text-gray-700">{user.fullName}</span>
                  {user?.role?.name && <p className="text-xs text-gray-500">{user.role.name}</p>}
                </div>
              )}
              <button
                onClick={handleLogout}
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors text-gray-700 hover:bg-red-50 hover:text-red-700 w-full"
              >
                <LogOut className="h-5 w-5 flex-shrink-0 text-gray-500" />
                {!sidebarCollapsed && <span>Logout</span>}
              </button>
            </div>
          </div>
        </nav>
      </aside>

      {/* Main content */}
      <main
        className={cn(
          'transition-all duration-300 pt-14 lg:pt-0',
          sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'
        )}
      >
        <div className="min-h-screen">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
